import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { LogIn, UserPlus, Mail, Lock, User, Loader2, AlertCircle } from "lucide-react";

export default function AuthModal({ onClose }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    let result;
    if (mode === "login") {
      result = await login(email, password);
    } else if (mode === "register") {
      result = await register(email, password, name);
    } else if (mode === "forgot") {
      result = await forgotPassword(resetEmail);
    }

    setLoading(false);
    if (mode === "forgot") {
      if (result.success) {
        setResetSuccess(true);
        setResetEmail("");
      } else {
        setError(result.error || "Erro desconhecido");
      }
    } else {
      if (result.success) {
        onClose?.();
      } else {
        setError(result.error || "Erro desconhecido");
      }
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await fetch("https://betmodel-backend-production-38b9.up.railway.app/api/auth/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to send reset email");
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div
        className="w-full max-w-sm mx-4 bg-surface border border-line rounded-sm p-6 animate-slide-up"
        data-testid="auth-modal"
      >
        {/* Tabs */}
        <div className="flex mb-6 border-b border-line">
          <button
            data-testid="auth-login-tab"
            onClick={() => { setMode("login"); setError(""); setResetSuccess(false); }}
            className={`flex-1 py-2 text-sm font-bold uppercase tracking-widest transition-colors ${
              mode === "login" ? "text-volt border-b-2 border-volt" : "text-txt-secondary"
            }`}
          >
            Entrar
          </button>
          <button
            data-testid="auth-register-tab"
            onClick={() => { setMode("register"); setError(""); setResetSuccess(false); }}
            className={`flex-1 py-2 text-sm font-bold uppercase tracking-widest transition-colors ${
              mode === "register" ? "text-volt border-b-2 border-volt" : "text-txt-secondary"
            }`}
          >
            Registar
          </button>
          {mode === "forgot" && (
            <button
              onClick={() => { setMode("login"); setError(""); setResetSuccess(false); }}
              className="text-sm text-txt-secondary hover:text-white transition-colors"
            >
              Voltar
            </button>
          )}
        </div>

        {mode === "forgot" && resetSuccess ? (
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center">
              <div className="bg-volt/20 p-3 rounded-full">
                <Mail className="w-8 h-8 text-volt" />
              </div>
            </div>
            <h3 className="text-white font-bold">Email enviado!</h3>
            <p className="text-txt-secondary">
              Verifique sua caixa de entrada para instruções de redefinição de senha.
              O link expira em 2 horas.
            </p>
            <button
              onClick={() => { setMode("login"); setResetSuccess(false); }}
              className="w-full bg-volt text-volt-fg font-bold uppercase tracking-wide px-6 py-2.5 rounded-sm transition-all hover:bg-volt-hover"
            >
              Voltar ao login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-secondary" />
                <input
                  data-testid="auth-name-input"
                  type="text"
                  placeholder="Nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-obsidian border border-line rounded-sm pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-txt-secondary/50 focus:border-volt focus:outline-none"
                />
              </div>
            )}

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-secondary" />
              <input
                data-testid="auth-email-input"
                type="email"
                placeholder="Email"
                value={mode === "forgot" ? resetEmail : email}
                onChange={(e) => {
                  if (mode === "forgot") setResetEmail(e.target.value);
                  else setEmail(e.target.value);
                }}
                required
                className="w-full bg-obsidian border border-line rounded-sm pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-txt-secondary/50 focus:border-volt focus:outline-none"
              />
            </div>

            {mode !== "forgot" && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-secondary" />
                <input
                  data-testid="auth-password-input"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-obsidian border border-line rounded-sm pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-txt-secondary/50 focus:border-volt focus:outline-none"
                />
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-danger text-xs" data-testid="auth-error">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {error}
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-volt text-volt-fg font-bold uppercase tracking-wide px-6 py-2.5 rounded-sm transition-all hover:bg-volt-hover active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "login" ? (
                <>
                  <LogIn className="w-4 h-4" />
                  Entrar
                </>
              ) : mode === "register" ? (
                <>
                  <UserPlus className="w-4 h-4" />
                  Criar Conta
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Enviar link de redefinição
                </>
              )}
            </button>
          </form>
        )}

        {!mode === "forgot" || !resetSuccess && (
          <div className="flex items-center justify-between mt-3 text-xs">
            {mode === "login" && (
              <button
                onClick={() => { setMode("forgot"); setError(""); }}
                className="text-txt-secondary hover:text-white transition-colors underline"
              >
                Esqueceu a senha?
              </button>
            )}
            <button
              data-testid="auth-skip-btn"
              onClick={onClose}
              className="w-full mt-3 text-xs text-txt-secondary hover:text-white transition-colors text-center py-2"
            >
              Continuar sem conta
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
